// X-axis label layout for cartesian charts (bar / line / area).
//
// Timestamp columns are detected and the tick shows a compact UTC date that grows
// only as needed — the year is added when values span years, the time when their
// time-of-day varies (e.g. "Aug 15", "Aug 15, 14:30", "Aug 15, 2026") — with the
// full timestamp on hover. UTC throughout because warehouse buckets are UTC-aligned;
// local formatting day-shifts and surfaces spurious offsets.
//
// For non-temporal categories: when labels are too wide to fit horizontally
// they overlap and recharts hides most of them, so — from the measured width —
// labels that fit stay horizontal (whole or ellipsised to a readable length),
// and tighter ones are angled.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { coerceLabel } from '@/_shared/lib/coerce-label';

dayjs.extend(utc);

/** Props we spread onto recharts' `<XAxis>`. Kept structural so this module
 *  does not pull in recharts types. */
export interface XAxisRenderProps {
  tickFormatter: (value: unknown, index: number) => string;
  interval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd' | 'equidistantPreserveStart';
  tickMargin?: number;
  angle?: number;
  textAnchor?: 'start' | 'middle' | 'end';
  height?: number;
}

export interface AxisConfig {
  props: XAxisRenderProps;
  /** Vertical space recharts reserves for the x-axis (its labels). Callers add
   *  this to a fixed plot height so angled labels grow the chart downward
   *  instead of shrinking the plot. */
  axisHeight: number;
  /** Full, readable label for tooltips. Present only for temporal axes — for
   *  categories recharts already shows the raw, untruncated label. */
  tooltipLabelFormatter?: (value: unknown) => string;
}

// The decision to angle/truncate is about whether labels fit the available plot
// width, not their raw length — two labels each own half the axis and fit fine;
// twenty don't.
const CHAR_PX = 7; // ~width of one character at the 12px tick font
const TICK_GAP_PX = 16; // min breathing room between two horizontal ticks
const MIN_HORIZONTAL_CHARS = 10; // below this, a horizontal ellipsis is too lossy → angle instead
const MAX_TICK_CHARS = 18; // cap for angled labels (and an overflow backstop)
const FALLBACK_AXIS_WIDTH = 480; // assumed container width until the real one is measured
const ANGLED_HEIGHT = 96; // reserved height for an 18-char label at -35° (+ margin), with slack for wide glyphs
const HORIZONTAL_HEIGHT = 30; // recharts' default <XAxis> height for upright labels

const DAY = 24 * 60 * 60 * 1000;

// Gate parsing strictly: V8's `Date.parse` is lenient enough to read "Department
// 7" as Dec 2007 or "May 5" as a date, which would misclassify category columns.
// ISO-8601-ish: "2026-06-15", "2026-06-15T14:30", "2026-06-15 14:30:00.000Z".
const ISO_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
// JS `Date.toString()`: "Mon Jun 15 2026 05:30:00 GMT+0530 (India Standard Time)".
// Warehouse `Date`s revived by the RPC client coerce to this form.
const DATE_TOSTRING_RE = /^[A-Z][a-z]{2} [A-Z][a-z]{2} \d{1,2} \d{4} \d{2}:\d{2}:\d{2} GMT[+-]\d{4}/;

/** Truncate long labels, preserving a trailing ellipsis. */
export function truncateLabel(value: string, max = MAX_TICK_CHARS): string {
  if (max <= 1) return value.length <= max ? value : '…';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}

/** Parse a label value to epoch millis, or null when it is not a date. Handles
 *  ISO strings and JS `Date.toString()` output (warehouse `Date`s arrive coerced
 *  to that form). */
export function parseTemporal(value: unknown): number | null {
  if (value == null) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!ISO_TIMESTAMP_RE.test(s) && !DATE_TOSTRING_RE.test(s)) return null;
  // `dayjs.utc` reads a zoneless ISO string (e.g. "2026-06-15T14:30") as UTC, where
  // `Date.parse` would read it in the local zone and re-introduce the day/time shift
  // the UTC formatting eliminates. Explicitly-zoned ISO and the `Date.toString()`
  // form already carry an offset, so dayjs honors it.
  const parsed = dayjs.utc(s);
  return parsed.isValid() ? parsed.valueOf() : null;
}

/** A column is temporal only when every non-empty value is a timestamp (a single
 *  value counts — a one-row panel still has a real timestamp). Requiring all of
 *  them avoids treating a mixed/category column as dates. */
export function detectTemporal(values: unknown[]): boolean {
  const nonEmpty = values.filter((v) => coerceLabel(v).trim() !== '');
  return nonEmpty.length > 0 && nonEmpty.every((v) => parseTemporal(v) != null);
}

/**
 * Format strings for the x-axis ticks and tooltip. The tooltip always shows the
 * full UTC timestamp; the tick stays minimal — it adds the year only when values
 * span more than one year, and the time only when their time-of-day varies (so
 * same-clock-time buckets like midnight render as a bare date).
 */
function timeFormats(times: number[]): { tick: string; full: string } {
  const first = times[0]!;
  // Second-of-day in UTC, ignoring milliseconds (the epoch is midnight-aligned).
  const secondOfDay = (t: number): number => Math.floor((t % DAY) / 1000);
  const sameTimeOfDay = times.every((t) => secondOfDay(t) === secondOfDay(first));
  const firstYear = dayjs.utc(first).year();
  const sameYear = times.every((t) => dayjs.utc(t).year() === firstYear);

  const date = sameYear ? 'MMM D' : 'MMM D, YYYY'; // Aug 15 / Aug 15, 2026
  return {
    tick: sameTimeOfDay ? date : `${date}, HH:mm`, // adds ", 14:30" when the time varies
    full: 'MMM D, YYYY, HH:mm [UTC]', // Aug 15, 2026, 14:30 UTC
  };
}

/** Assemble XAxis props, angling when the labels don't fit horizontally. */
function axisProps(tickFormatter: XAxisRenderProps['tickFormatter'], angled: boolean): XAxisRenderProps {
  const props: XAxisRenderProps = { tickFormatter, interval: 'preserveStartEnd', tickMargin: 8 };
  if (angled) {
    props.angle = -35;
    props.textAnchor = 'end';
    props.height = ANGLED_HEIGHT;
  }
  return props;
}

/**
 * Build XAxis props (+ tooltip formatter for temporal axes) from the label-column
 * values.
 *
 * `width` is the measured container width in px, taken as the plot width the
 * labels live in. When omitted (e.g. before the first measurement) a typical
 * container width is assumed.
 */
export function buildXAxisConfig(rawValues: unknown[], width?: number): AxisConfig {
  const plotWidth = width && width > 0 ? width : FALLBACK_AXIS_WIDTH;
  const count = Math.max(rawValues.length, 1);
  const charsPerSlot = Math.floor((plotWidth / count - TICK_GAP_PX) / CHAR_PX);

  // Temporal: format timestamps compactly (already short, so never truncated);
  // angle only when even the compact labels don't fit. Size the fit decision off
  // the formatted length, so a verbose "Mon Jun 15 2026 …" that renders as
  // "Jun 15" isn't needlessly angled.
  if (detectTemporal(rawValues)) {
    const times = rawValues.map(parseTemporal).filter((t): t is number => t != null);
    const { tick, full } = timeFormats(times);
    const tickFormatter = (value: unknown): string => {
      const t = parseTemporal(value);
      return t == null ? coerceLabel(value) : dayjs.utc(t).format(tick);
    };
    const maxLen = rawValues.reduce<number>((m, v) => Math.max(m, tickFormatter(v).length), 0);
    const angled = maxLen > charsPerSlot;
    return {
      props: axisProps(tickFormatter, angled),
      axisHeight: angled ? ANGLED_HEIGHT : HORIZONTAL_HEIGHT,
      tooltipLabelFormatter: (value) => {
        const t = parseTemporal(value);
        return t == null ? coerceLabel(value) : dayjs.utc(t).format(full);
      },
    };
  }

  // Categorical: fit whole, ellipsise to the slot, or angle when too tight.
  const maxLen = rawValues.reduce<number>((m, v) => Math.max(m, coerceLabel(v).length), 0);
  const fitsFull = maxLen <= charsPerSlot;
  const angled = !fitsFull && charsPerSlot < MIN_HORIZONTAL_CHARS;
  const cap = angled ? MAX_TICK_CHARS : fitsFull ? maxLen : charsPerSlot;
  return {
    props: axisProps((value) => truncateLabel(coerceLabel(value), cap), angled),
    axisHeight: angled ? ANGLED_HEIGHT : HORIZONTAL_HEIGHT,
  };
}
